import { type ColumnDef } from "@tanstack/react-table";
import type { JobPosting } from "@/services/api/job-postings";
import { format } from "date-fns";

interface ColumnOptions {
  onTitleClick?: (posting: JobPosting) => void;
}

function TitleCell({
  row,
  onTitleClick,
}: {
  row: { original: JobPosting };
  onTitleClick?: (posting: JobPosting) => void;
}) {
  const { title } = row.original;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onTitleClick?.(row.original);
      }}
      className="text-left text-blue-600 hover:text-blue-800 hover:underline truncate cursor-pointer"
    >
      {title}
    </button>
  );
}

export function useJobPostingColumns(
  options?: ColumnOptions
): ColumnDef<JobPosting>[] {
  const { onTitleClick } = options ?? {};

  return [
    {
      accessorKey: "title",
      header: "Title",
      size: 300,
      cell: ({ row }) => (
        <TitleCell row={row} onTitleClick={onTitleClick} />
      ),
    },
    {
      accessorKey: "company",
      header: "Company",
      size: 180,
      cell: ({ getValue }) => (
        <span className="truncate">{getValue<string>() ?? "—"}</span>
      ),
    },
    {
      accessorKey: "location",
      header: "Location",
      size: 160,
      cell: ({ getValue }) => (
        <span className="truncate">{getValue<string>() ?? "—"}</span>
      ),
    },

    {
      accessorKey: "sourceName",
      header: "Source",
      size: 120,
      cell: ({ getValue }) => (
        <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
          {getValue<string>()}
        </span>
      ),
    },
    {
      accessorKey: "firstSeenAt",
      header: "First Seen",
      size: 120,
      cell: ({ getValue }) =>
        format(new Date(getValue<string>()), "MMM d, yyyy"),
    },
    {
      accessorKey: "lastSeenAt",
      header: "Last Seen",
      size: 120,
      cell: ({ getValue }) =>
        format(new Date(getValue<string>()), "MMM d, yyyy"),
    },
    {
      accessorKey: "isActive",
      header: "Status",
      size: 100,
      cell: ({ getValue }) => {
        const isActive = getValue<boolean>();
        return (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              isActive ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
            }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
  ];
}
